import * as React from 'react'
import { styled } from 'baseui'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'
import { ResizeSizeItem } from './types'

interface ResizeSizeRowProps {
  item?: ResizeSizeItem
  name: string
  description?: string
  size?: { width: number; height: number }
  icon?: (props?: any) => JSX.Element
  active?: boolean
  onClick?: () => void
  premiumBadge?: React.ReactNode
  rightAdornment?: React.ReactNode
  appendUnitLabel?: boolean
  unitLabel?: string
  className?: string
  style?: React.CSSProperties
  expandedContent?: React.ReactNode
}

function computeIconSize(size?: { width: number; height: number }) {
  let iconW = 50
  let iconH = 50
  if (size) {
    if (size.width > size.height) {
      iconW = 50
      iconH = (size.height / size.width) * 50
    } else {
      iconH = 50
      iconW = (size.width / size.height) * 50
    }
  }
  return { iconW: Math.max(iconW, 16), iconH: Math.max(iconH, 16) }
}

export function ResizeSizeRow({
  item,
  name,
  description,
  size,
  icon,
  active = false,
  onClick,
  premiumBadge,
  rightAdornment,
  appendUnitLabel = true,
  unitLabel = 'px',
  className,
  style,
  expandedContent,
}: ResizeSizeRowProps) {
  const { t } = useTranslation()
  const resolvedName = t(item?.name ?? name)
  const resolvedDescription = item?.description ?? description ? t(item?.description ?? description!) : undefined
  const resolvedSize = item?.size ?? size
  const resolvedIcon = item?.icon ?? icon
  const Icon = resolvedIcon ?? null

  const { iconW, iconH } = computeIconSize(resolvedSize)

  return (
    <RowContainer $active={active} onClick={onClick} className={className} style={style}>
      <MainRow>
        <div
          style={{
            padding: '8px',
            width: '66px',
            display: 'flex',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: `${iconH}px`,
              width: `${iconW}px`,
              background: lightTheme.colors.grayScale100,
              borderRadius: '4px',
            }}
          >
            {Icon ? <Icon /> : null}
          </div>
        </div>

        <span
          style={{
            position: 'absolute',
            left: '66px',
            top: '8px',
            bottom: '9px',
            borderRight: '1px solid rgba(229, 229, 229, 1)',
          }}
        />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '16px 0 16px 8px',
            gap: '2px',
            minWidth: 0,
          }}
        >
          <p
            style={{
              margin: 0,
              ...lightTheme.typography.Small11medium,
              color: active ? lightTheme.colors.primary : lightTheme.colors.blackGray,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {resolvedName}
          </p>
          {resolvedDescription ? (
            <p
              style={{
                margin: 0,
                ...lightTheme.typography.Small11medium,
                color: lightTheme.colors.contentPrimary,
              }}
            >
              {resolvedDescription}
              {appendUnitLabel ? ` ${unitLabel}` : ''}
            </p>
          ) : null}
        </div>

        {premiumBadge ? (
          <div style={{ position: 'absolute', top: '4px', right: '4px', zIndex: 1 }}>
            {premiumBadge}
          </div>
        ) : null}

        {rightAdornment ? (
          <div
            style={{
              position: 'absolute',
              right: '8px',
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {rightAdornment}
          </div>
        ) : null}
      </MainRow>
      {expandedContent ? <ExpandedContent>{expandedContent}</ExpandedContent> : null}
    </RowContainer>
  )
}

const RowContainer = styled('div', (props: { $active?: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: props.$active ? `2px solid ${lightTheme.colors.primary}` : lightTheme.borders.borderDefault,
  borderRadius: '8px',
  minHeight: '66px',
  cursor: 'pointer',
  position: 'relative',
  boxSizing: 'border-box',
  ':hover': {
    background: props.$active ? lightTheme.colors.white : lightTheme.colors.grayScale50,
  },
}))

const MainRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '62px',
  position: 'relative',
  flexShrink: 0,
})

const ExpandedContent = styled('div', {
  width: '100%',
  overflow: 'hidden',
  animationDuration: '280ms',
  animationName: {
    from: { opacity: 0, transform: 'translateY(-4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animationDuration: '0ms',
  },
})
